import { css } from '@styled-system/css';
import {
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { type FC } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { useTheme } from '@/app/providers/ThemeProvider';
import { Message } from '@/shared/components/ui/Message';
import { Spinner } from '@/shared/components/ui/Spinner';
import { dsTokens } from '@/shared/theme/tokens';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

// ============================================================================
// Activité quotidienne — courbes lissées ventes / articles ajoutés, palette DS.
// ⚠️ Alimenté par des mocks tant que la table de snapshots n'existe pas côté
// backend (chantier « analysis_stats_snapshot » de la roadmap v2).
// ============================================================================

export interface DailyActivityPoint {
  date: string;
  sales: number;
  added: number;
}

interface DailyActivityChartProps {
  data: DailyActivityPoint[] | null;
  loading?: boolean;
}

const containerStyles = css({
  width: '100%',
  position: 'relative',
});

const centered = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '260px',
});

export const DailyActivityChart: FC<DailyActivityChartProps> = ({ data, loading }) => {
  const { t, i18n } = useTranslation();
  const { theme: colorMode } = useTheme();
  const ds = dsTokens[colorMode];
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';

  if (loading) {
    return (
      <div className={centered}>
        <Spinner size={24} />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Message type="info" bordered>
        {t('statsPanel.noActivity')}
      </Message>
    );
  }

  // Fenêtre d'affichage : le graphe est TRONQUÉ au dernier jour qui porte de
  // la donnée d'ajouts (borne de cohorte : le snapshot est figé à J-7 de la
  // création, les jours suivants n'ont que des ventes). Sans cette troncature,
  // la courbe orange s'arrêtait en plein milieu du graphe — effet « coupé ».
  let lastAddedIdx = -1;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].added > 0) {
      lastAddedIdx = i;
      break;
    }
  }
  const points = lastAddedIdx >= 0 ? data.slice(Math.max(0, lastAddedIdx - 6), lastAddedIdx + 1) : data.slice(-7);

  const labels = points.map((point) =>
    new Date(point.date).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' }),
  );

  const brand = ds['brand.primary'];
  const added = ds['warning.default'];

  const chartData: ChartData<'line'> = {
    labels,
    datasets: [
      {
        label: t('statsPanel.seriesSales'),
        data: points.map((point) => point.sales),
        borderColor: brand,
        backgroundColor: `${brand}1A`,
        fill: true,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: brand,
        pointBorderColor: ds['surface.default'],
        pointHoverBorderWidth: 2,
      },
      {
        label: t('statsPanel.seriesAdded'),
        data: points.map((point) => point.added),
        borderColor: added,
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: added,
        pointBorderColor: ds['surface.default'],
        pointHoverBorderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          color: ds['text.secondary'],
          boxWidth: 18,
          boxHeight: 2,
          font: { size: 11 },
        },
      },
      tooltip: {
        backgroundColor: ds['surface.elevated'],
        titleColor: ds['text.primary'],
        bodyColor: ds['text.secondary'],
        borderColor: ds['border.default'],
        borderWidth: 1,
        padding: 8,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: ds['text.tertiary'],
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 10,
        },
        border: { color: ds['border.default'] },
      },
      y: {
        beginAtZero: true,
        grid: { color: `${ds['border.subtle']}80` },
        ticks: { color: ds['text.tertiary'], font: { size: 11 }, precision: 0 },
        border: { display: false },
      },
    },
  };

  return (
    <div className={containerStyles} style={{ height: 260 }}>
      <Line data={chartData} options={options} />
    </div>
  );
};
